import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import {
  IzzyPayload,
  IzzyInboundMessage,
  ParsedMessageSection,
  EarlyRejectionSection,
  ConversationContextSection,
  UserContextSection,
  RequestDecisionSection,
  KnowledgeSection,
  ResponseSection,
  FactCheckSection,
  RelevantConversation,
  UserFact,
  KnowledgeFact,
  FactCheckIssue,
  ReplyChainMessage
} from 'mj_generatedentities';
import { marked } from 'marked';

/**
 * Pipeline stage configuration for visual rendering
 */
interface PipelineStage {
  id: string;
  title: string;
  icon: string;
  colorClass: string;
  order: number;
}

/**
 * Payload Viewer Component
 *
 * A beautiful, world-class visualization of the Izzy AI processing pipeline.
 * Transforms raw IzzyPayload JSON into an intuitive, scannable narrative
 * showing how Izzy processed each message through its agent pipeline.
 *
 * Features:
 * - Visual pipeline flow with connecting lines
 * - Collapsible sections for each processing stage
 * - Status indicators (complete, in-progress, failed)
 * - Color-coded stages for quick scanning
 * - Responsive design with smooth animations
 */
@Component({
  standalone: false,
  selector: 'izzy-payload-viewer',
  templateUrl: './payload-viewer.component.html',
  styleUrls: ['./payload-viewer.component.css']
})
export class PayloadViewerComponent implements OnChanges {

  @Input() payload: IzzyPayload | null = null;
  @Input() compactMode: boolean = false;

  constructor(private sanitizer: DomSanitizer) {}

  // Section collapse state
  protected expandedSections: Set<string> = new Set([
    'inbound', 'parsed', 'earlyRejection', 'decision', 'response', 'factCheck'
  ]);

  // Track which fact objects are expanded (by category+key)
  protected expandedFacts: Set<string> = new Set();

  // Track which conversation items are expanded (by conversationId)
  protected expandedConversations: Set<string> = new Set();

  // Pipeline stages configuration
  protected readonly pipelineStages: PipelineStage[] = [
    { id: 'inbound', title: 'Inbound Message', icon: 'fa-envelope', colorClass: 'stage-inbound', order: 1 },
    { id: 'parsed', title: 'Message Parsed', icon: 'fa-scissors', colorClass: 'stage-parsed', order: 2 },
    { id: 'earlyRejection', title: 'Early Filter', icon: 'fa-shield-halved', colorClass: 'stage-filter', order: 3 },
    { id: 'conversation', title: 'Conversation Context', icon: 'fa-comments', colorClass: 'stage-context', order: 4 },
    { id: 'user', title: 'User Context', icon: 'fa-user-circle', colorClass: 'stage-user', order: 5 },
    { id: 'decision', title: 'Request Decision', icon: 'fa-gavel', colorClass: 'stage-decision', order: 6 },
    { id: 'knowledge', title: 'Knowledge Gathered', icon: 'fa-book-open', colorClass: 'stage-knowledge', order: 7 },
    { id: 'response', title: 'Response Written', icon: 'fa-pen-fancy', colorClass: 'stage-response', order: 8 },
    { id: 'factCheck', title: 'Fact Check', icon: 'fa-check-double', colorClass: 'stage-factcheck', order: 9 }
  ];

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['payload'] && this.payload) {
      // Reset expanded sections based on what's available
      this.expandedSections = new Set(['inbound', 'parsed', 'earlyRejection', 'decision', 'response', 'factCheck']);

      // In compact mode, collapse more sections
      if (this.compactMode) {
        this.expandedSections = new Set(['inbound', 'response']);
      }
    }
  }

  // ========== Section Toggle ==========

  toggleSection(sectionId: string): void {
    if (this.expandedSections.has(sectionId)) {
      this.expandedSections.delete(sectionId);
    } else {
      this.expandedSections.add(sectionId);
    }
  }

  isSectionExpanded(sectionId: string): boolean {
    return this.expandedSections.has(sectionId);
  }

  // ========== Status Helpers ==========

  getSectionStatus(sectionId: string): 'complete' | 'incomplete' | 'passed' | 'failed' | 'warning' {
    if (!this.payload) return 'incomplete';

    switch (sectionId) {
      case 'inbound':
        return this.payload.inboundMessage ? 'complete' : 'incomplete';

      case 'parsed':
        return this.payload.parsedMessage?.complete ? 'complete' : 'incomplete';

      case 'earlyRejection':
        if (!this.payload.earlyRejection?.complete) return 'incomplete';
        return this.payload.earlyRejection.isObviousGarbage ? 'failed' : 'passed';

      case 'conversation':
        return this.payload.conversationContext?.complete ? 'complete' : 'incomplete';

      case 'user':
        return this.payload.userContext?.complete ? 'complete' : 'incomplete';

      case 'decision':
        if (!this.payload.decision?.complete) return 'incomplete';
        return this.payload.decision.canProcess ? 'passed' : 'failed';

      case 'knowledge':
        return this.payload.knowledge?.complete ? 'complete' : 'incomplete';

      case 'response':
        return this.payload.response?.complete ? 'complete' : 'incomplete';

      case 'factCheck':
        if (!this.payload.factCheck?.complete) return 'incomplete';
        if (this.payload.factCheck.passed) return 'passed';
        if (this.payload.factCheck.issues?.some(i => i.severity === 'error')) return 'failed';
        return 'warning';

      default:
        return 'incomplete';
    }
  }

  getStatusIcon(status: string): string {
    switch (status) {
      case 'complete':
      case 'passed':
        return 'fa-circle-check';
      case 'failed':
        return 'fa-circle-xmark';
      case 'warning':
        return 'fa-triangle-exclamation';
      case 'incomplete':
      default:
        return 'fa-circle-dot';
    }
  }

  getStatusClass(status: string): string {
    switch (status) {
      case 'complete':
      case 'passed':
        return 'status-success';
      case 'failed':
        return 'status-error';
      case 'warning':
        return 'status-warning';
      case 'incomplete':
      default:
        return 'status-pending';
    }
  }

  // ========== Inbound Message Helpers ==========

  get inboundMessage(): IzzyInboundMessage | null {
    return this.payload?.inboundMessage || null;
  }

  getEmailInitials(email: string): string {
    if (!email) return '?';
    const parts = email.split('@')[0].split('.');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return email[0].toUpperCase();
  }

  // ========== Parsed Message Helpers ==========

  get parsedMessage(): ParsedMessageSection | null {
    return this.payload?.parsedMessage || null;
  }

  get replyChain(): ReplyChainMessage[] {
    return this.payload?.parsedMessage?.replyChain || [];
  }

  // ========== Early Rejection Helpers ==========

  get earlyRejection(): EarlyRejectionSection | null {
    return this.payload?.earlyRejection || null;
  }

  getGarbageTypeIcon(type: string | undefined): string {
    switch (type) {
      case 'spam': return 'fa-ban';
      case 'automated-message': return 'fa-robot';
      case 'prank': return 'fa-face-grin-tongue-wink';
      case 'phishing': return 'fa-fish';
      default: return 'fa-circle-xmark';
    }
  }

  formatGarbageType(type: string | undefined): string {
    if (!type) return 'Unknown';
    return type.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
  }

  // ========== Conversation Context Helpers ==========

  get conversationContext(): ConversationContextSection | null {
    return this.payload?.conversationContext || null;
  }

  get relevantConversations(): RelevantConversation[] {
    return this.payload?.conversationContext?.relevantConversations || [];
  }

  formatConversationDate(date: Date | string): string {
    if (!date) return '';
    const d = new Date(date);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) +
           ' @ ' + d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  }

  truncateText(text: string, maxLength: number = 100): string {
    if (!text) return '';
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  }

  /**
   * Toggle expansion state for a conversation item
   */
  toggleConversationExpansion(conversationId: string): void {
    if (this.expandedConversations.has(conversationId)) {
      this.expandedConversations.delete(conversationId);
    } else {
      this.expandedConversations.add(conversationId);
    }
  }

  /**
   * Check if a conversation item is expanded
   */
  isConversationExpanded(conversationId: string): boolean {
    return this.expandedConversations.has(conversationId);
  }

  /**
   * Check if a conversation has expandable content (full inbound message differs from summary/truncated)
   */
  hasExpandableContent(conv: RelevantConversation): boolean {
    // If there's an inboundSummary, it means the inboundMessage was truncated
    if (conv.inboundSummary) return true;
    // Also expandable if inboundMessage or response is long
    if (conv.inboundMessage && conv.inboundMessage.length > 100) return true;
    if (conv.response && conv.response.length > 100) return true;
    return false;
  }

  // ========== User Context Helpers ==========

  get userContext(): UserContextSection | null {
    return this.payload?.userContext || null;
  }

  get userFacts(): UserFact[] {
    return this.payload?.userContext?.facts || [];
  }

  getFactsByCategory(): Map<string, UserFact[]> {
    const factsByCategory = new Map<string, UserFact[]>();
    for (const fact of this.userFacts) {
      const category = fact.category || 'Other';
      if (!factsByCategory.has(category)) {
        factsByCategory.set(category, []);
      }
      factsByCategory.get(category)!.push(fact);
    }
    return factsByCategory;
  }

  formatFactValue(value: unknown): string {
    if (value === null || value === undefined) return '-';
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (typeof value === 'object') {
      if (Array.isArray(value)) {
        return value.length > 0 ? `${value.length} items` : 'None';
      }
      return 'Object';
    }
    if (value instanceof Date) {
      return new Date(value).toLocaleDateString();
    }
    return String(value);
  }

  /**
   * Check if a fact value is an object that can be expanded
   */
  isExpandableValue(value: unknown): boolean {
    if (value === null || value === undefined) return false;
    if (typeof value !== 'object') return false;
    // Objects and non-empty arrays are expandable
    return true;
  }

  /**
   * Get a unique key for a fact (for tracking expansion state)
   */
  getFactKey(category: string, key: string): string {
    return `${category}::${key}`;
  }

  /**
   * Toggle expansion state for a fact's object value
   */
  toggleFactExpansion(category: string, key: string): void {
    const factKey = this.getFactKey(category, key);
    if (this.expandedFacts.has(factKey)) {
      this.expandedFacts.delete(factKey);
    } else {
      this.expandedFacts.add(factKey);
    }
  }

  /**
   * Check if a fact's object value is expanded
   */
  isFactExpanded(category: string, key: string): boolean {
    return this.expandedFacts.has(this.getFactKey(category, key));
  }

  /**
   * Format an object value as pretty-printed JSON for display
   */
  formatObjectValue(value: unknown): string {
    try {
      return JSON.stringify(value, null, 2);
    } catch {
      return String(value);
    }
  }

  getCategoryIcon(category: string): string {
    const lower = category.toLowerCase();
    if (lower.includes('member')) return 'fa-id-card';
    if (lower.includes('event') || lower.includes('registration')) return 'fa-calendar-check';
    if (lower.includes('course') || lower.includes('learning')) return 'fa-graduation-cap';
    if (lower.includes('preference')) return 'fa-sliders';
    if (lower.includes('profile')) return 'fa-user';
    if (lower.includes('payment') || lower.includes('billing')) return 'fa-credit-card';
    return 'fa-tag';
  }

  // ========== Request Decision Helpers ==========

  get requestDecision(): RequestDecisionSection | null {
    return this.payload?.decision || null;
  }

  getRejectionCategoryIcon(category: string | undefined): string {
    switch (category) {
      case 'legal-advice': return 'fa-scale-balanced';
      case 'financial-dispute': return 'fa-money-bill-transfer';
      case 'explicit-human-request': return 'fa-user-check';
      case 'urgent-sensitive-personal': return 'fa-user-shield';
      case 'urgent-sensitive-business': return 'fa-building-shield';
      case 'policy-exception': return 'fa-file-contract';
      case 'complex-technical': return 'fa-microchip';
      case 'password-reset-failure': return 'fa-key';
      case 'marketing-speaker': return 'fa-bullhorn';
      case 'inappropriate': return 'fa-triangle-exclamation';
      case 'out-of-scope': return 'fa-circle-question';
      default: return 'fa-hand';
    }
  }

  formatRejectionCategory(category: string | undefined): string {
    if (!category) return 'Unknown';
    return category.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
  }

  // ========== Knowledge Helpers ==========

  get knowledgeSection(): KnowledgeSection | null {
    return this.payload?.knowledge || null;
  }

  get knowledgeFacts(): KnowledgeFact[] {
    return this.payload?.knowledge?.facts || [];
  }

  getConfidenceClass(confidence: number | undefined): string {
    if (confidence === undefined) return '';
    if (confidence >= 0.85) return 'confidence-high';
    if (confidence >= 0.6) return 'confidence-medium';
    return 'confidence-low';
  }

  formatConfidence(confidence: number | undefined): string {
    if (confidence === undefined) return '';
    return Math.round(confidence * 100) + '%';
  }

  /**
   * Render markdown/HTML content safely
   */
  renderMarkdown(content: string | null | undefined): SafeHtml {
    if (!content) {
      return this.sanitizer.bypassSecurityTrustHtml('');
    }
    // Check if content looks like HTML (has tags)
    if (content.includes('<') && content.includes('>')) {
      return this.sanitizer.bypassSecurityTrustHtml(content);
    }
    // Otherwise render as markdown
    const html = marked.parse(content, { async: false }) as string;
    return this.sanitizer.bypassSecurityTrustHtml(html);
  }

  // ========== Response Helpers ==========

  get responseSection(): ResponseSection | null {
    return this.payload?.response || null;
  }

  // ========== Fact Check Helpers ==========

  get factCheckSection(): FactCheckSection | null {
    return this.payload?.factCheck || null;
  }

  get factCheckIssues(): FactCheckIssue[] {
    return this.payload?.factCheck?.issues || [];
  }

  getIssueSeverityClass(severity: string): string {
    return severity === 'error' ? 'issue-error' : 'issue-warning';
  }

  getIssueSeverityIcon(severity: string): string {
    return severity === 'error' ? 'fa-circle-xmark' : 'fa-triangle-exclamation';
  }
}

/**
 * Utility function to prevent tree-shaking
 */
export function LoadPayloadViewerComponent(): void {
  // Intentionally empty
}
